import axios from 'axios';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { QueryClient } from '@tanstack/react-query';

import type { Config, ImageData, RiskData, Submission } from '../models';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const getConfig = async () => {
  const { data } = await axios.get<Config>('/config/config.json');

  return data;
};

interface AWS {
  AWS_URL: string;
  PRESIGNED_POST: {
    url: string;
    fields: {
      key: string;
      acl: string;
      bucket: string;
      policy: string;
      'Content-Type': string;
      'x-amz-algorithm': string;
      'x-amz-credential': string;
      'x-amz-date': string;
      'x-amz-security-token': string;
      'x-amz-signature': string;
    };
  };
}

const getTokenUpload = async (config: Config, url: string, observationUuid: string) => {
  const uploadUrl = `${url}?observation_uuid=${observationUuid}`;

  const { data } = await axios.get<AWS>(uploadUrl, {
    baseURL: config.ENDPOINT,
  });

  return data;
};

const uploadImage = async ({ PRESIGNED_POST, AWS_URL }: AWS, file: Blob) => {
  const formData = new FormData();

  formData.append('x-amz-algorithm', PRESIGNED_POST.fields['x-amz-algorithm']);
  formData.append('x-amz-signature', PRESIGNED_POST.fields['x-amz-signature']);
  formData.append('x-amz-security-token', PRESIGNED_POST.fields['x-amz-security-token']);
  formData.append('x-amz-date', PRESIGNED_POST.fields['x-amz-date']);
  formData.append('x-amz-credential', PRESIGNED_POST.fields['x-amz-credential']);
  formData.append('Content-Type', PRESIGNED_POST.fields['Content-Type']);
  formData.append('policy', PRESIGNED_POST.fields.policy);
  formData.append('key', PRESIGNED_POST.fields.key);
  formData.append('acl', PRESIGNED_POST.fields.acl);
  formData.append('file', file);

  await axios.post<void>(PRESIGNED_POST.url, formData);

  return AWS_URL;
};

const uploadImages = (config: Config, images: ImageData[], observationUuid: string) =>
  Promise.all(
    images.map((image) =>
      getTokenUpload(config, image.url, observationUuid).then((data) =>
        uploadImage(data, image.file),
      ),
    ),
  );

export const submitRiskForm = async (config: Config, riskFormData: RiskData) => {
  const {
    contactEmail,
    contactPhone,
    firstName,
    lastName,
    location,
    description,
    observationDate,
    images,
    hasSampleNumber,
    sampleNumber,
    captcha,
    howDidYouHearAboutUs,
  } = riskFormData;

  if (!captcha) {
    throw new Error('reCAPTCHA verification is required. Please complete the reCAPTCHA challenge.');
  }

  const { lat, long } = ((locations) => {
    const [lat, long] = locations.split(',');

    return {
      lat: parseFloat(lat),
      long: parseFloat(long),
    };
  })(location);

  const observationUuid = uuidv4();

  const payload: Submission = {
    requested_datetime: dayjs(observationDate, 'YYYY-MM-DD').format(),
    media_url: images ? (await uploadImages(config, images, observationUuid)).join(',') : '',
    observation_uuid: observationUuid,
    first_name: firstName,
    last_name: lastName,
    email: contactEmail,
    phone: contactPhone,
    recaptcha_token: captcha,
    attributes: {
      observation_date: dayjs(observationDate, 'YYYY-MM-DD').format(),
      description,
      first_name: firstName,
      last_name: lastName,
      email: contactEmail,
      phone: contactPhone,
      report_date: dayjs().format(),
      sample_number: hasSampleNumber ? sampleNumber : undefined,
      how_did_you_hear_about_us: howDidYouHearAboutUs,
    },
  };

  if (!isNaN(lat) && !isNaN(long)) {
    payload.lat = lat;
    payload.long = long;
  }

  return axios.post('/submit', payload, {
    baseURL: config.ENDPOINT,
  });
};
